import { useFormContext, useFieldArray } from 'react-hook-form'
import { Plus, Trash2, GripVertical } from 'lucide-react'
import { Button } from '@/components/serge'
import { FormField } from '@/components/serge/FormField'
import { type LegalEntityFormData } from '@/lib/legalEntitySchema'

export function SoftwareStep() {
  const {
    register,
    control,
    formState: { errors },
    watch,
  } = useFormContext<LegalEntityFormData>()

  const toOptionalNumber = (value: string) => {
    if (value === '') return undefined
    const parsed = Number(value)
    return Number.isNaN(parsed) ? undefined : parsed
  }

  const {
    fields: softwareFields,
    append: appendSoftware,
    remove: removeSoftware,
  } = useFieldArray({
    control,
    name: 'softwareUsed',
  })

  const {
    fields: paymentFields,
    append: appendPayment,
    remove: removePayment,
  } = useFieldArray({
    control,
    name: 'paymentSchedule',
  })

  const isSubcontractor = watch('isSubcontractor')

  return (
    <div className="space-y-6">
      {/* Logiciels et licences */}
      <div className="border border-border bg-surface p-5">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-1">Logiciels et licences</h3>
            <p className="text-sm text-foreground-secondary">
              Logiciels tiers utilisés dans votre activité
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => appendSoftware({ name: '', version: '', license: '', url: '' })}
            className="gap-2"
          >
            <Plus className="w-4 h-4" />
            Ajouter
          </Button>
        </div>

        {softwareFields.length === 0 ? (
          <div className="text-center py-8 text-sm text-foreground-muted">
            Aucun logiciel ajouté. Cliquez sur "Ajouter" pour commencer.
          </div>
        ) : (
          <div className="space-y-3">
            {softwareFields.map((field, index) => (
              <div
                key={field.id}
                className="border border-border bg-bg p-4 space-y-3 group hover:border-primary/50 transition"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <GripVertical className="w-4 h-4 text-foreground-muted opacity-0 group-hover:opacity-100 transition cursor-grab" />
                    <span className="text-xs font-mono text-foreground-muted uppercase tracking-widest">
                      Logiciel #{index + 1}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeSoftware(index)}
                    className="text-xs text-red-400 hover:text-red-300 transition flex items-center gap-1"
                  >
                    <Trash2 className="w-3 h-3" />
                    Supprimer
                  </button>
                </div>

                <FormField label="Nom" required error={errors.softwareUsed?.[index]?.name}>
                  <input
                    {...register(`softwareUsed.${index}.name`)}
                    className="w-full border border-border bg-bg p-2 text-sm text-foreground focus:outline-none focus:border-primary transition"
                    placeholder="Ex: PostgreSQL, React, Stripe SDK..."
                  />
                </FormField>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <FormField label="Version" error={errors.softwareUsed?.[index]?.version}>
                    <input
                      {...register(`softwareUsed.${index}.version`)}
                      className="w-full border border-border bg-bg p-2 text-sm text-foreground font-mono focus:outline-none focus:border-primary transition"
                      placeholder="Ex: 15.2, v18.3.1"
                    />
                  </FormField>

                  <FormField label="Licence" error={errors.softwareUsed?.[index]?.license}>
                    <input
                      {...register(`softwareUsed.${index}.license`)}
                      className="w-full border border-border bg-bg p-2 text-sm text-foreground focus:outline-none focus:border-primary transition"
                      placeholder="Ex: MIT, Apache 2.0, GPL-3.0"
                    />
                  </FormField>
                </div>

                <FormField
                  label="Lien vers la licence"
                  error={errors.softwareUsed?.[index]?.url}
                  hint="URL vers les conditions de la licence"
                >
                  <input
                    type="url"
                    {...register(`softwareUsed.${index}.url`)}
                    className="w-full border border-border bg-bg p-2 text-sm text-foreground focus:outline-none focus:border-primary transition"
                    placeholder="https://..."
                  />
                </FormField>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Sous-traitance */}
      <div className="border border-border bg-surface p-5">
        <h3 className="text-lg font-semibold text-foreground mb-1">Contrat de sous-traitance</h3>
        <p className="text-sm text-foreground-secondary mb-6">
          Si vous intervenez en tant que sous-traitant
        </p>

        <div className="space-y-4">
          <div className="border border-border bg-bg p-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                {...register('isSubcontractor')}
                className="w-4 h-4 border-border text-primary focus:ring-primary"
              />
              <div>
                <div className="text-sm font-medium text-foreground">
                  Activer le contrat cadre de sous-traitance
                </div>
                <div className="text-xs text-foreground-muted mt-0.5">
                  Cochez si vous proposez des prestations en sous-traitance
                </div>
              </div>
            </label>
          </div>

          {isSubcontractor && (
            <div className="space-y-4 animate-fade-in">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  label="Périmètre / Scope"
                  error={errors.masterServiceAgreement?.scope}
                  tooltip="Définition du périmètre d'intervention"
                >
                  <textarea
                    {...register('masterServiceAgreement.scope')}
                    className="w-full min-h-[100px] border border-border bg-bg p-3 text-sm text-foreground focus:outline-none focus:border-primary transition resize-y"
                    placeholder="Ex: Développement d'applications web, intégration API..."
                  />
                </FormField>

                <FormField
                  label="SLA / Niveaux de service"
                  error={errors.masterServiceAgreement?.sla}
                  tooltip="Engagements de qualité et délais"
                >
                  <textarea
                    {...register('masterServiceAgreement.sla')}
                    className="w-full min-h-[100px] border border-border bg-bg p-3 text-sm text-foreground focus:outline-none focus:border-primary transition resize-y"
                    placeholder="Ex: Temps de réponse 24h, disponibilité 99.9%..."
                  />
                </FormField>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  label="Critères d'acceptation"
                  error={errors.masterServiceAgreement?.acceptance}
                  tooltip="Conditions de validation des livrables"
                >
                  <textarea
                    {...register('masterServiceAgreement.acceptance')}
                    className="w-full min-h-[100px] border border-border bg-bg p-3 text-sm text-foreground focus:outline-none focus:border-primary transition resize-y"
                    placeholder="Ex: Tests unitaires > 80%, validation fonctionnelle..."
                  />
                </FormField>

                <FormField
                  label="Propriété intellectuelle"
                  error={errors.masterServiceAgreement?.ipOwnership}
                  tooltip="Conditions de cession des droits d'auteur"
                >
                  <textarea
                    {...register('masterServiceAgreement.ipOwnership')}
                    className="w-full min-h-[100px] border border-border bg-bg p-3 text-sm text-foreground focus:outline-none focus:border-primary transition resize-y"
                    placeholder="Ex: Cession des droits au client, licence exclusive..."
                  />
                </FormField>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  label="Confidentialité"
                  error={errors.masterServiceAgreement?.confidentiality}
                  tooltip="Clause de confidentialité et NDA"
                >
                  <textarea
                    {...register('masterServiceAgreement.confidentiality')}
                    className="w-full min-h-[100px] border border-border bg-bg p-3 text-sm text-foreground focus:outline-none focus:border-primary transition resize-y"
                    placeholder="Ex: Engagement de confidentialité pendant 5 ans..."
                  />
                </FormField>

                <FormField
                  label="Plafond de responsabilité"
                  error={errors.masterServiceAgreement?.liabilityCap}
                  tooltip="Limitation de responsabilité en cas de préjudice"
                >
                  <textarea
                    {...register('masterServiceAgreement.liabilityCap')}
                    className="w-full min-h-[100px] border border-border bg-bg p-3 text-sm text-foreground focus:outline-none focus:border-primary transition resize-y"
                    placeholder="Ex: Responsabilité limitée au montant du contrat..."
                  />
                </FormField>
              </div>

              <FormField
                label="Modalités de résiliation"
                error={errors.masterServiceAgreement?.termination}
                tooltip="Conditions de rupture du contrat"
              >
                <textarea
                  {...register('masterServiceAgreement.termination')}
                  className="w-full min-h-[100px] border border-border bg-bg p-3 text-sm text-foreground focus:outline-none focus:border-primary transition resize-y"
                  placeholder="Ex: Résiliation avec préavis de 3 mois, indemnité en cas de rupture anticipée..."
                />
              </FormField>

              <div className="border border-border bg-bg p-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    {...register('masterServiceAgreement.subcontractingAllowed')}
                    className="w-4 h-4 border-border text-primary focus:ring-primary"
                  />
                  <div className="text-sm text-foreground">Sous-traitance autorisée</div>
                </label>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Échéancier de paiement */}
      {isSubcontractor && (
        <div className="border border-border bg-surface p-5 animate-fade-in">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-1">Échéancier de paiement</h3>
              <p className="text-sm text-foreground-secondary">
                Jalons et conditions de paiement
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                appendPayment({ label: '', amount: undefined, percentage: undefined, dueDate: '', trigger: '' })
              }
              className="gap-2"
            >
              <Plus className="w-4 h-4" />
              Ajouter un jalon
            </Button>
          </div>

          {paymentFields.length === 0 ? (
            <div className="text-center py-8 text-sm text-foreground-muted">
              Aucun jalon défini. Cliquez sur "Ajouter un jalon" pour commencer.
            </div>
          ) : (
            <div className="space-y-3">
              {paymentFields.map((field, index) => (
                <div
                  key={field.id}
                  className="border border-border bg-bg p-4 space-y-3 group hover:border-primary/50 transition"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <GripVertical className="w-4 h-4 text-foreground-muted opacity-0 group-hover:opacity-100 transition cursor-grab" />
                      <span className="text-xs font-mono text-foreground-muted uppercase tracking-widest">
                        Jalon #{index + 1}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removePayment(index)}
                      className="text-xs text-red-400 hover:text-red-300 transition flex items-center gap-1"
                    >
                      <Trash2 className="w-3 h-3" />
                      Supprimer
                    </button>
                  </div>

                  <FormField label="Libellé" required error={errors.paymentSchedule?.[index]?.label}>
                    <input
                      {...register(`paymentSchedule.${index}.label`)}
                      className="w-full border border-border bg-bg p-2 text-sm text-foreground focus:outline-none focus:border-primary transition"
                      placeholder="Ex: Acompte à la commande, Livraison intermédiaire..."
                    />
                  </FormField>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <FormField label="Montant (€)" error={errors.paymentSchedule?.[index]?.amount}>
                      <input
                        type="number"
                        {...register(`paymentSchedule.${index}.amount`, { setValueAs: toOptionalNumber })}
                        className="w-full border border-border bg-bg p-2 text-sm text-foreground font-mono focus:outline-none focus:border-primary transition"
                        placeholder="5000"
                      />
                    </FormField>

                    <FormField label="Pourcentage (%)" error={errors.paymentSchedule?.[index]?.percentage}>
                      <input
                        type="number"
                        {...register(`paymentSchedule.${index}.percentage`, { setValueAs: toOptionalNumber })}
                        className="w-full border border-border bg-bg p-2 text-sm text-foreground font-mono focus:outline-none focus:border-primary transition"
                        placeholder="30"
                        min="0"
                        max="100"
                      />
                    </FormField>

                    <FormField label="Date d'échéance" error={errors.paymentSchedule?.[index]?.dueDate}>
                      <input
                        type="text"
                        {...register(`paymentSchedule.${index}.dueDate`)}
                        className="w-full border border-border bg-bg p-2 text-sm text-foreground focus:outline-none focus:border-primary transition"
                        placeholder="J+30, À la livraison..."
                      />
                    </FormField>
                  </div>

                  <FormField
                    label="Condition / Déclencheur"
                    error={errors.paymentSchedule?.[index]?.trigger}
                    hint="Événement qui déclenche le paiement"
                  >
                    <input
                      {...register(`paymentSchedule.${index}.trigger`)}
                      className="w-full border border-border bg-bg p-2 text-sm text-foreground focus:outline-none focus:border-primary transition"
                      placeholder="Ex: Validation du cahier des charges, mise en production..."
                    />
                  </FormField>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
